import { useState, useCallback, useEffect } from 'react';
import { type Language, t } from './i18n';
import { LanguageSelector } from './components/LanguageSelector';
import { LandingForm } from './components/LandingForm';
import { StartupForm } from './components/StartupForm';
import { ProjectForm } from './components/ProjectForm';
import { ChatInterface } from './components/ChatInterface';
import { LeadCapture, type Lead } from './components/LeadCapture';
import { ReportDisplay } from './components/ReportDisplay';
import { StartupReportDisplay } from './components/StartupReportDisplay';
import { ProjectReportDisplay } from './components/ProjectReportDisplay';
import { AdminPanel } from './components/AdminPanel';
import { ErrorBoundary, AppErrorFallback } from './components/ErrorBoundary';
import { useChat, generateReport, generateStartupReport, generateProjectReport, type FormData, type StartupFormData, type ProjectFormData } from './hooks/useChat';
import { ThemeToggle } from './components/ui/theme-toggle';

/**
 * Detect browser language and return 'it' for Italian locales, 'en' otherwise
 */
function detectBrowserLanguage(): Language {
  const browserLang = navigator.language || (navigator as { userLanguage?: string }).userLanguage || 'en';
  return browserLang.toLowerCase().startsWith('it') ? 'it' : 'en';
}

type AppState = 'landing' | 'chat' | 'leadCapture' | 'report';

// Use relative URLs in production (same origin), localhost only for local dev
const API_URL = (import.meta as unknown as { env: Record<string, string> }).env.VITE_API_URL ??
  (window.location.hostname === 'localhost' ? 'http://localhost:3001' : '');

// Scan chat messages for an email the user may have shared mid-conversation.
// Scoped to user messages so the assistant's example text never misleads us.
const EMAIL_IN_TEXT = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i;
function findEmailInUserMessages(messages: { role: string; content: string }[]): string | null {
  for (const m of messages) {
    if (m.role !== 'user') continue;
    const hit = m.content.match(EMAIL_IN_TEXT);
    if (hit) return hit[0].toLowerCase();
  }
  return null;
}

function navigate(path: string) {
  window.history.pushState(null, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function HomePage() {
  const [language, setLanguage] = useState<Language>(() => detectBrowserLanguage());

  const cards = [
    {
      key: 'automation',
      href: '/automation-assessment',
      iconBg: 'bg-automation-600',
      borderColor: 'border-l-automation-200',
      ctaColor: 'text-automation-700 dark:text-automation-500',
      hoverBorder: 'hover:border-automation-300',
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
        </svg>
      ),
    },
    {
      key: 'startup',
      href: '/startup',
      iconBg: 'bg-startup-600',
      borderColor: 'border-l-startup-200',
      ctaColor: 'text-startup-700 dark:text-startup-500',
      hoverBorder: 'hover:border-startup-300',
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 0 1-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 0 0 6.16-12.12A14.98 14.98 0 0 0 9.631 8.41m5.96 5.96a14.926 14.926 0 0 1-5.841 2.58m-.119-8.54a6 6 0 0 0-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 0 0-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 0 1-2.448-2.448 14.9 14.9 0 0 1 .06-.312m-2.24 2.39a4.493 4.493 0 0 0-1.757 4.306 4.493 4.493 0 0 0 4.306-1.758M16.5 9a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z" />
        </svg>
      ),
    },
    {
      key: 'project',
      href: '/project',
      iconBg: 'bg-project-600',
      borderColor: 'border-l-project-200',
      ctaColor: 'text-project-700 dark:text-project-500',
      hoverBorder: 'hover:border-project-300',
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6.429 9.75 2.25 12l4.179 2.25m0-4.5 5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0 4.179 2.25L12 21.75 2.25 16.5l4.179-2.25m11.142 0-5.571 3-5.571-3" />
        </svg>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-foreground rounded-lg flex items-center justify-center">
              <span className="text-background font-bold text-lg">N</span>
            </div>
            <span className="font-semibold text-foreground text-lg">NextProcess</span>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <LanguageSelector language={language} onLanguageChange={setLanguage} />
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4 tracking-tight">
            {t(language, 'home.title')}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {t(language, 'home.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {cards.map((card) => (
            <button
              key={card.key}
              onClick={() => navigate(card.href)}
              className={`card p-6 text-left border-l-4 ${card.borderColor} ${card.hoverBorder} hover:shadow-md transition-all duration-150 group cursor-pointer`}
            >
              <div className={`w-14 h-14 ${card.iconBg} rounded-xl flex items-center justify-center text-white mb-5 group-hover:scale-105 transition-transform`}>
                {card.icon}
              </div>
              <h2 className="text-lg font-semibold text-foreground mb-2">
                {t(language, `home.${card.key}.title`)}
              </h2>
              <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                {t(language, `home.${card.key}.description`)}
              </p>
              <span className={`text-sm font-medium ${card.ctaColor} group-hover:underline`}>
                {t(language, `home.${card.key}.cta`)} &rarr;
              </span>
            </button>
          ))}
        </div>
      </main>

      <footer className="bg-card border-t border-border mt-auto">
        <div className="max-w-6xl mx-auto px-4 py-6 text-center text-sm text-muted-foreground">
          <p>NextProcess AI Assessment Platform</p>
          <p className="mt-1">Powered by AI</p>
        </div>
      </footer>
    </div>
  );
}

// Simple path-based routing
function useRoute() {
  const [route, setRoute] = useState(window.location.pathname);

  useEffect(() => {
    const handlePopState = () => setRoute(window.location.pathname);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return route;
}

function MainApp() {
  const [language, setLanguage] = useState<Language>(() => detectBrowserLanguage());
  const [appState, setAppState] = useState<AppState>('landing');
  const [formData, setFormData] = useState<FormData | null>(null);
  const [_lead, setLead] = useState<Lead | null>(null);
  const [assessmentId, setAssessmentId] = useState<string | null>(null);
  const [report, setReport] = useState<unknown>(null);
  const [reportLoading, setReportLoading] = useState(false);
  // Email captured at intake (optional) — pre-fills LeadCapture so the user doesn't retype.
  // Updated from intake form; mid-chat extraction happens server-side and is read back on submit.
  const [prospectEmail, setProspectEmail] = useState<string>('');

  const {
    messages,
    isLoading,
    isThinking,
    isComplete,
    error,
    scores: chatScores,
    tokenUsage,
    violations,
    currentSuggestions,
    userMessageCount,
    canForceComplete,
    pendingAutoComplete,
    sendMessage,
    initChat,
    forceComplete,
    reset: resetChat,
  } = useChat({
    formData: formData ?? {
      companySize: '',
      revenueRange: '',
      industry: '',
      geography: [],
      softwareTools: [],
      role: '',
    },
    language,
    sessionId: assessmentId ?? undefined,
  });

  // Initialize chat when entering chat state
  useEffect(() => {
    if (appState === 'chat' && messages.length === 0 && formData) {
      initChat();
    }
  }, [appState, messages.length, formData, initChat]);

  // Handle browser back button with confirmation
  useEffect(() => {
    if (appState === 'chat' && messages.length > 0) {
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        e.preventDefault();
        e.returnValue = '';
      };

      const handlePopState = (e: PopStateEvent) => {
        e.preventDefault();
        const confirmMessage = language === 'it'
          ? 'Tornare indietro resetterà la conversazione. Sei sicuro?'
          : 'Going back will reset the conversation. Are you sure?';

        if (window.confirm(confirmMessage)) {
          handleNewAssessment();
        } else {
          // Push state back to prevent navigation
          window.history.pushState(null, '', window.location.href);
        }
      };

      // Push initial state
      window.history.pushState(null, '', window.location.href);
      window.addEventListener('popstate', handlePopState);
      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        window.removeEventListener('popstate', handlePopState);
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [appState, messages.length, language]);

  // Generate session UUID, init assessment in backend, and go to chat
  const handleFormSubmit = useCallback(async (data: FormData, contactEmail?: string) => {
    const sessionId = crypto.randomUUID();
    setAssessmentId(sessionId);
    setFormData(data);
    if (contactEmail) setProspectEmail(contactEmail);

    // Early save: Initialize assessment in backend
    try {
      await fetch(`${API_URL}/api/assessments/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: sessionId,
          assessmentType: 'automation',
          formData: {
            companySize: data.companySize,
            industry: data.industry,
            geography: data.geography,
            role: data.role,
            softwareTools: data.softwareTools,
          },
          ...(contactEmail ? { lead: { email: contactEmail } } : {}),
        }),
      });
      console.log('Assessment initialized in backend:', sessionId);
    } catch (err) {
      console.error('Failed to initialize assessment:', err);
      // Continue anyway - we can still save later
    }

    setAppState('chat');
    console.log('New assessment session:', sessionId);
  }, []);

  // When chat completes, go to lead capture. Scan user messages for an email so the
  // lead form pre-fills if the prospect shared it during the conversation.
  const handleChatComplete = useCallback(() => {
    if (!prospectEmail) {
      const fromChat = findEmailInUserMessages(messages);
      if (fromChat) setProspectEmail(fromChat);
    }
    setAppState('leadCapture');
  }, [messages, prospectEmail]);

  // Internal function to generate report (called after lead capture)
  const handleGenerateReportInternal = useCallback(async (leadData: Lead) => {
    if (!formData) return;

    setReportLoading(true);
    try {
      const generatedReport = await generateReport(messages, formData, language, chatScores);
      setReport(generatedReport);

      // Format conversation for storage
      const conversation = messages.map(msg => ({
        role: msg.role as 'user' | 'assistant',
        content: msg.content,
        timestamp: new Date().toISOString(),
      }));

      // Calculate estimated cost from token usage
      // Gemini 2.5 Flash pricing: ~$0.075/1M input, ~$0.30/1M output tokens
      const estimatedCostUsd = tokenUsage ? (
        (tokenUsage.promptTokens * 0.000000075) +
        (tokenUsage.completionTokens * 0.0000003)
      ) : 0;

      // Type the report for extraction
      const report = generatedReport as {
        scores?: {
          raw_total?: number;
          adjusted_total?: number;
          categories?: {
            process_maturity?: { score?: number };
            data_readiness?: { score?: number };
            technology?: { score?: number };
            organization?: { score?: number };
            strategy?: { score?: number };
          };
        };
        metrics?: {
          maturity_score?: number;
          sustainability_score?: number;  // backward compat
          optimization_potential_percent?: number;
        };
        economic_analysis?: {
          wasted_time?: { hours_per_week?: number };
          roi_potential?: { estimated_annual_savings?: number };
        };
        summary?: {
          key_gaps?: string[];
          key_strengths?: string[];
        };
        opportunities?: Array<{ area?: string; description?: string }>;
        recommendations?: {
          immediate_actions?: Array<{ action?: string }>;
        };
      };

      // Extract real scores from report
      const categories = report.scores?.categories || {};
      const assessmentData = {
        id: assessmentId || crypto.randomUUID(),
        createdAt: new Date().toISOString(),
        lead: leadData,
        formData: {
          companySize: formData.companySize,
          industry: formData.industry,
          geography: formData.geography,
          role: formData.role,
          softwareTools: formData.softwareTools,
        },
        scores: {
          dataEntry: Math.round((categories.data_readiness?.score || 0) / 4), // 0-20 -> 0-5
          salesMarketing: Math.round((categories.organization?.score || 0) / 4), // 0-20 -> 0-5
          accounting: Math.round((categories.process_maturity?.score || 0) / 5), // 0-25 -> 0-5
          projectMgmt: Math.round((categories.strategy?.score || 0) / 3), // 0-15 -> 0-5
          supplyChain: Math.round((categories.technology?.score || 0) / 3), // 0-15 -> 0-5
          overall: report.metrics?.maturity_score || report.metrics?.sustainability_score || report.scores?.adjusted_total || 0,
        },
        metrics: {
          estimatedHoursWasted: report.economic_analysis?.wasted_time?.hours_per_week || 20,
          potentialSavingsPercent: Math.round(report.metrics?.optimization_potential_percent || 35),
          estimatedAnnualCost: report.economic_analysis?.roi_potential?.estimated_annual_savings || 0,
        },
        painPoints: report.summary?.key_gaps || [
          'Manual data entry identified',
          'Process inefficiencies detected',
        ],
        quickWins: (report.opportunities || []).slice(0, 3).map(o => o.area || o.description || 'Automation opportunity'),
        recommendations: (report.recommendations?.immediate_actions || []).map(a => a.action || 'Process improvement'),
        conversation,
        report: generatedReport,
        tokenUsage: tokenUsage ? {
          promptTokens: tokenUsage.promptTokens,
          completionTokens: tokenUsage.completionTokens,
          totalTokens: tokenUsage.totalTokens,
          estimatedCostUsd,
        } : undefined,
        status: 'complete' as const,
        violations: violations ?? undefined,
      };

      // Save assessment in backend
      try {
        await fetch(`${API_URL}/api/assessments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(assessmentData),
        });
        console.log('Assessment saved:', assessmentId);
      } catch (saveErr) {
        console.error('Failed to save assessment:', saveErr);
      }

      setAppState('report');
    } catch (err) {
      console.error('Failed to generate report:', err);
    } finally {
      setReportLoading(false);
    }
  }, [messages, formData, assessmentId, language, tokenUsage, chatScores, violations]);

  // After lead capture, generate report
  const handleLeadCapture = useCallback(async (leadData: Lead) => {
    if (!formData || !assessmentId) return;

    setLead(leadData);

    // Generate the report
    await handleGenerateReportInternal(leadData);
  }, [formData, assessmentId, handleGenerateReportInternal]);

  // Called when user clicks "Genera Report" in chat
  const handleGenerateReport = useCallback(() => {
    handleChatComplete();
  }, [handleChatComplete]);

  const handleNewAssessment = useCallback(() => {
    resetChat();
    setFormData(null);
    setLead(null);
    setAssessmentId(null);
    setReport(null);
    setAppState('landing');
  }, [resetChat]);

  return (
    <ErrorBoundary fallback={<AppErrorFallback language={language} onReset={handleNewAssessment} />}>
      <div className="min-h-screen bg-background flow-automation">
        {/* Header */}
        <header className="bg-card border-b border-border sticky top-0 z-10">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">N</span>
              </div>
              <span className="font-semibold text-foreground text-lg">NextProcess</span>
            </div>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <LanguageSelector language={language} onLanguageChange={setLanguage} />
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-6xl mx-auto px-4 py-8">
          <ErrorBoundary>
            {appState === 'landing' && (
              <LandingForm language={language} onSubmit={handleFormSubmit} />
            )}

            {appState === 'chat' && (
              <ChatInterface
                language={language}
                messages={messages}
                isLoading={isLoading}
                isThinking={isThinking}
                isComplete={isComplete}
                error={error}
                currentSuggestions={currentSuggestions}
                canForceComplete={canForceComplete}
                pendingAutoComplete={pendingAutoComplete}
                userMessageCount={userMessageCount}
                onSendMessage={sendMessage}
                onGenerateReport={handleGenerateReport}
                onForceComplete={forceComplete}
              />
            )}

            {appState === 'leadCapture' && (
              <LeadCapture
                language={language}
                onSubmit={handleLeadCapture}
                isLoading={reportLoading}
                isAfterChat={true}
                initialLead={prospectEmail ? { email: prospectEmail } : undefined}
              />
            )}

            {appState === 'report' && (
              <ReportDisplay
                language={language}
                report={report as Parameters<typeof ReportDisplay>[0]['report']}
                isLoading={reportLoading}
                onNewAssessment={handleNewAssessment}
              />
            )}
          </ErrorBoundary>
        </main>

        {/* Footer */}
        <footer className="bg-card border-t border-border mt-auto">
          <div className="max-w-6xl mx-auto px-4 py-6 text-center text-sm text-muted-foreground">
            <p>NextProcess Automation Maturity Assessment</p>
            <p className="mt-1">Powered by AI</p>
          </div>
        </footer>
      </div>
    </ErrorBoundary>
  );
}

type StartupAppState = 'landing' | 'chat' | 'leadCapture' | 'report';

function StartupApp() {
  const [language, setLanguage] = useState<Language>(() => detectBrowserLanguage());
  const [appState, setAppState] = useState<StartupAppState>('landing');
  const [formData, setFormData] = useState<StartupFormData | null>(null);
  const [_lead, setLead] = useState<Lead | null>(null);
  const [assessmentId, setAssessmentId] = useState<string | null>(null);
  const [report, setReport] = useState<unknown>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [prospectEmail, setProspectEmail] = useState<string>('');

  const {
    messages,
    isLoading,
    isThinking,
    isComplete,
    error,
    scores: chatScores,
    tokenUsage,
    violations,
    currentSuggestions,
    userMessageCount,
    canForceComplete,
    pendingAutoComplete,
    sendMessage,
    initChat,
    forceComplete,
    reset: resetChat,
  } = useChat({
    formData: formData ?? {
      ideaDescription: '',
      projectStage: 'idea' as const,
      targetSector: '',
      founderBackground: 'technical' as const,
      teamSize: 'solo' as const,
      investmentReadiness: 'bootstrapping' as const,
      geographicFocus: [],
      timeline: 'exploring' as const,
      hasExistingProduct: false,
    },
    language,
    sessionId: assessmentId ?? undefined,
    chatEndpoint: '/api/startup-chat',
  });

  useEffect(() => {
    if (appState === 'chat' && messages.length === 0 && formData) {
      initChat();
    }
  }, [appState, messages.length, formData, initChat]);

  useEffect(() => {
    if (appState === 'chat' && messages.length > 0) {
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        e.preventDefault();
        e.returnValue = '';
      };
      const handlePopState = (e: PopStateEvent) => {
        e.preventDefault();
        const confirmMessage = language === 'it'
          ? 'Tornare indietro resetterà la conversazione. Sei sicuro?'
          : 'Going back will reset the conversation. Are you sure?';
        if (window.confirm(confirmMessage)) {
          handleNewAssessment();
        } else {
          window.history.pushState(null, '', window.location.href);
        }
      };
      window.history.pushState(null, '', window.location.href);
      window.addEventListener('popstate', handlePopState);
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => {
        window.removeEventListener('popstate', handlePopState);
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [appState, messages.length, language]);

  const handleFormSubmit = useCallback(async (data: StartupFormData, contactEmail?: string) => {
    const sessionId = crypto.randomUUID();
    setAssessmentId(sessionId);
    setFormData(data);
    if (contactEmail) setProspectEmail(contactEmail);
    try {
      await fetch(`${API_URL}/api/assessments/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: sessionId,
          assessmentType: 'startup',
          formData: data,
          ...(contactEmail ? { lead: { email: contactEmail } } : {}),
        }),
      });
    } catch (err) {
      console.error('Failed to initialize startup assessment:', err);
    }
    setAppState('chat');
    console.log('New startup assessment session:', sessionId);
  }, []);

  const handleChatComplete = useCallback(() => {
    if (!prospectEmail) {
      const fromChat = findEmailInUserMessages(messages);
      if (fromChat) setProspectEmail(fromChat);
    }
    setAppState('leadCapture');
  }, [messages, prospectEmail]);

  const handleGenerateReportInternal = useCallback(async (leadData: Lead) => {
    if (!formData) return;
    setReportLoading(true);
    try {
      const generatedReport = await generateStartupReport(
        messages,
        formData,
        language,
        chatScores as Record<string, unknown> | null
      );
      setReport(generatedReport);

      const conversation = messages.map(msg => ({
        role: msg.role as 'user' | 'assistant',
        content: msg.content,
        timestamp: new Date().toISOString(),
      }));

      const estimatedCostUsd = tokenUsage ? (
        (tokenUsage.promptTokens * 0.000000075) +
        (tokenUsage.completionTokens * 0.0000003)
      ) : 0;

      const reportScores = (generatedReport as { scores?: Record<string, unknown> })?.scores ?? {};
      const assessmentData = {
        id: assessmentId || crypto.randomUUID(),
        createdAt: new Date().toISOString(),
        assessmentType: 'startup',
        lead: leadData,
        formData,
        scores: { ...(chatScores ?? {}), ...reportScores },
        metrics: {},
        painPoints: [],
        quickWins: [],
        recommendations: [],
        conversation,
        report: generatedReport,
        tokenUsage: tokenUsage ? {
          promptTokens: tokenUsage.promptTokens,
          completionTokens: tokenUsage.completionTokens,
          totalTokens: tokenUsage.totalTokens,
          estimatedCostUsd,
        } : undefined,
        status: 'complete' as const,
        violations: violations ?? undefined,
      };

      try {
        await fetch(`${API_URL}/api/assessments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(assessmentData),
        });
        console.log('Startup assessment saved:', assessmentId);
      } catch (saveErr) {
        console.error('Failed to save startup assessment:', saveErr);
      }

      setAppState('report');
    } catch (err) {
      console.error('Failed to generate startup report:', err);
    } finally {
      setReportLoading(false);
    }
  }, [messages, formData, assessmentId, language, tokenUsage, chatScores, violations]);

  const handleLeadCapture = useCallback(async (leadData: Lead) => {
    if (!formData || !assessmentId) return;
    setLead(leadData);
    await handleGenerateReportInternal(leadData);
  }, [formData, assessmentId, handleGenerateReportInternal]);

  const handleGenerateReport = useCallback(() => {
    handleChatComplete();
  }, [handleChatComplete]);

  const handleNewAssessment = useCallback(() => {
    resetChat();
    setFormData(null);
    setLead(null);
    setAssessmentId(null);
    setReport(null);
    setAppState('landing');
  }, [resetChat]);

  return (
    <ErrorBoundary fallback={<AppErrorFallback language={language} onReset={handleNewAssessment} />}>
      <div className="min-h-screen bg-background flow-startup">
        <header className="bg-card border-b border-border sticky top-0 z-10">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">N</span>
              </div>
              <span className="font-semibold text-foreground text-lg">NextProcess</span>
            </div>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <LanguageSelector language={language} onLanguageChange={setLanguage} />
            </div>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-4 py-8">
          <ErrorBoundary>
            {appState === 'landing' && (
              <StartupForm language={language} onSubmit={handleFormSubmit} />
            )}

            {appState === 'chat' && (
              <ChatInterface
                language={language}
                messages={messages}
                isLoading={isLoading}
                isThinking={isThinking}
                isComplete={isComplete}
                error={error}
                currentSuggestions={currentSuggestions}
                canForceComplete={canForceComplete}
                pendingAutoComplete={pendingAutoComplete}
                userMessageCount={userMessageCount}
                onSendMessage={sendMessage}
                onGenerateReport={handleGenerateReport}
                onForceComplete={forceComplete}
                assessmentType="startup"
              />
            )}

            {appState === 'leadCapture' && (
              <LeadCapture
                language={language}
                onSubmit={handleLeadCapture}
                isLoading={reportLoading}
                isAfterChat={true}
                assessmentType="startup"
                initialLead={prospectEmail ? { email: prospectEmail } : undefined}
              />
            )}

            {appState === 'report' && (
              <StartupReportDisplay
                language={language}
                report={report as Parameters<typeof StartupReportDisplay>[0]['report']}
                isLoading={reportLoading}
                onNewAssessment={handleNewAssessment}
              />
            )}
          </ErrorBoundary>
        </main>

        <footer className="bg-card border-t border-border mt-auto">
          <div className="max-w-6xl mx-auto px-4 py-6 text-center text-sm text-muted-foreground">
            <p>{t(language, 'startup.footer')}</p>
            <p className="mt-1">Powered by AI</p>
          </div>
        </footer>
      </div>
    </ErrorBoundary>
  );
}

type ProjectAppState = 'landing' | 'chat' | 'leadCapture' | 'report';

function ProjectApp() {
  const [language, setLanguage] = useState<Language>(() => detectBrowserLanguage());
  const [appState, setAppState] = useState<ProjectAppState>('landing');
  const [formData, setFormData] = useState<ProjectFormData | null>(null);
  const [_lead, setLead] = useState<Lead | null>(null);
  const [assessmentId, setAssessmentId] = useState<string | null>(null);
  const [report, setReport] = useState<unknown>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [prospectEmail, setProspectEmail] = useState<string>('');

  const {
    messages,
    isLoading,
    isThinking,
    isComplete,
    error,
    scores: chatScores,
    tokenUsage,
    violations,
    currentSuggestions,
    userMessageCount,
    canForceComplete,
    pendingAutoComplete,
    sendMessage,
    initChat,
    forceComplete,
    reset: resetChat,
  } = useChat({
    formData: formData ?? {
      projectDescription: '',
      projectType: 'web-app' as const,
      targetUsers: 'external' as const,
      industry: '',
      companySize: '2-10' as const,
      budget: '',
      timeline: '',
      technicalTeam: 'none' as const,
      existingSystems: '',
      hasDesign: false,
      geographicFocus: [],
    },
    language,
    sessionId: assessmentId ?? undefined,
    chatEndpoint: '/api/project-chat',
  });

  useEffect(() => {
    if (appState === 'chat' && messages.length === 0 && formData) {
      initChat();
    }
  }, [appState, messages.length, formData, initChat]);

  useEffect(() => {
    if (appState === 'chat' && messages.length > 0) {
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        e.preventDefault();
        e.returnValue = '';
      };
      const handlePopState = (e: PopStateEvent) => {
        e.preventDefault();
        const confirmMessage = language === 'it'
          ? 'Tornare indietro resetterà la conversazione. Sei sicuro?'
          : 'Going back will reset the conversation. Are you sure?';
        if (window.confirm(confirmMessage)) {
          handleNewAssessment();
        } else {
          window.history.pushState(null, '', window.location.href);
        }
      };
      window.history.pushState(null, '', window.location.href);
      window.addEventListener('popstate', handlePopState);
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => {
        window.removeEventListener('popstate', handlePopState);
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [appState, messages.length, language]);

  const handleFormSubmit = useCallback(async (data: ProjectFormData, contactEmail?: string) => {
    const sessionId = crypto.randomUUID();
    setAssessmentId(sessionId);
    setFormData(data);
    if (contactEmail) setProspectEmail(contactEmail);
    try {
      await fetch(`${API_URL}/api/assessments/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: sessionId,
          assessmentType: 'project',
          formData: data,
          ...(contactEmail ? { lead: { email: contactEmail } } : {}),
        }),
      });
    } catch (err) {
      console.error('Failed to initialize project assessment:', err);
    }
    setAppState('chat');
    console.log('New project assessment session:', sessionId);
  }, []);

  const handleChatComplete = useCallback(() => {
    if (!prospectEmail) {
      const fromChat = findEmailInUserMessages(messages);
      if (fromChat) setProspectEmail(fromChat);
    }
    setAppState('leadCapture');
  }, [messages, prospectEmail]);

  const handleGenerateReportInternal = useCallback(async (leadData: Lead) => {
    if (!formData) return;
    setReportLoading(true);
    try {
      const generatedReport = await generateProjectReport(
        messages,
        formData,
        language,
        chatScores as Record<string, unknown> | null
      );
      setReport(generatedReport);

      const conversation = messages.map(msg => ({
        role: msg.role as 'user' | 'assistant',
        content: msg.content,
        timestamp: new Date().toISOString(),
      }));

      const estimatedCostUsd = tokenUsage ? (
        (tokenUsage.promptTokens * 0.000000075) +
        (tokenUsage.completionTokens * 0.0000003)
      ) : 0;

      const reportScores = (generatedReport as { scores?: Record<string, unknown> })?.scores ?? {};
      const assessmentData = {
        id: assessmentId || crypto.randomUUID(),
        createdAt: new Date().toISOString(),
        assessmentType: 'project',
        lead: leadData,
        formData,
        scores: { ...(chatScores ?? {}), ...reportScores },
        metrics: {},
        painPoints: [],
        quickWins: [],
        recommendations: [],
        conversation,
        report: generatedReport,
        tokenUsage: tokenUsage ? {
          promptTokens: tokenUsage.promptTokens,
          completionTokens: tokenUsage.completionTokens,
          totalTokens: tokenUsage.totalTokens,
          estimatedCostUsd,
        } : undefined,
        status: 'complete' as const,
        violations: violations ?? undefined,
      };

      try {
        await fetch(`${API_URL}/api/assessments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(assessmentData),
        });
        console.log('Project assessment saved:', assessmentId);
      } catch (saveErr) {
        console.error('Failed to save project assessment:', saveErr);
      }

      setAppState('report');
    } catch (err) {
      console.error('Failed to generate project report:', err);
    } finally {
      setReportLoading(false);
    }
  }, [messages, formData, assessmentId, language, tokenUsage, chatScores, violations]);

  const handleLeadCapture = useCallback(async (leadData: Lead) => {
    if (!formData || !assessmentId) return;
    setLead(leadData);
    await handleGenerateReportInternal(leadData);
  }, [formData, assessmentId, handleGenerateReportInternal]);

  const handleGenerateReport = useCallback(() => {
    handleChatComplete();
  }, [handleChatComplete]);

  const handleNewAssessment = useCallback(() => {
    resetChat();
    setFormData(null);
    setLead(null);
    setAssessmentId(null);
    setReport(null);
    setAppState('landing');
  }, [resetChat]);

  return (
    <ErrorBoundary fallback={<AppErrorFallback language={language} onReset={handleNewAssessment} />}>
      <div className="min-h-screen bg-background flow-project">
        <header className="bg-card border-b border-border sticky top-0 z-10">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">N</span>
              </div>
              <span className="font-semibold text-foreground text-lg">NextProcess</span>
            </div>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <LanguageSelector language={language} onLanguageChange={setLanguage} />
            </div>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-4 py-8">
          <ErrorBoundary>
            {appState === 'landing' && (
              <ProjectForm language={language} onSubmit={handleFormSubmit} />
            )}

            {appState === 'chat' && (
              <ChatInterface
                language={language}
                messages={messages}
                isLoading={isLoading}
                isThinking={isThinking}
                isComplete={isComplete}
                error={error}
                currentSuggestions={currentSuggestions}
                canForceComplete={canForceComplete}
                pendingAutoComplete={pendingAutoComplete}
                userMessageCount={userMessageCount}
                onSendMessage={sendMessage}
                onGenerateReport={handleGenerateReport}
                onForceComplete={forceComplete}
                assessmentType="project"
              />
            )}

            {appState === 'leadCapture' && (
              <LeadCapture
                language={language}
                onSubmit={handleLeadCapture}
                isLoading={reportLoading}
                isAfterChat={true}
                assessmentType="project"
                initialLead={prospectEmail ? { email: prospectEmail } : undefined}
              />
            )}

            {appState === 'report' && (
              <ProjectReportDisplay
                language={language}
                report={report as Parameters<typeof ProjectReportDisplay>[0]['report']}
                isLoading={reportLoading}
                onNewAssessment={handleNewAssessment}
              />
            )}
          </ErrorBoundary>
        </main>

        <footer className="bg-card border-t border-border mt-auto">
          <div className="max-w-6xl mx-auto px-4 py-6 text-center text-sm text-muted-foreground">
            <p>{t(language, 'project.footer')}</p>
            <p className="mt-1">Powered by AI</p>
          </div>
        </footer>
      </div>
    </ErrorBoundary>
  );
}

/**
 * Read-only shareable report view. Renders the appropriate report component based
 * on assessmentType, fetched via the unauthenticated /api/public-report/:id endpoint.
 * Admin can copy/share this URL to prospects (e.g. via WhatsApp).
 */
function PublicReport({ reportId }: { reportId: string }) {
  const [language, setLanguage] = useState<Language>(() => detectBrowserLanguage());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payload, setPayload] = useState<{
    assessmentType: 'automation' | 'startup' | 'project';
    report: unknown;
    formData: unknown;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/public-report/${encodeURIComponent(reportId)}`);
        if (!res.ok) {
          if (!cancelled) setError(res.status === 404 ? 'not_found' : 'server_error');
          return;
        }
        const data = await res.json();
        if (!cancelled) setPayload(data);
      } catch {
        if (!cancelled) setError('network_error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  const isItalian = language === 'it';

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="bg-card border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded flex items-center justify-center text-primary-foreground font-bold">
              N
            </div>
            <span className="font-semibold text-foreground">NextProcess</span>
          </a>
          <LanguageSelector language={language} onLanguageChange={setLanguage} />
        </div>
      </header>

      <main className="flex-1 max-w-6xl mx-auto w-full px-4 py-8">
        {loading && (
          <div className="text-center py-16">
            <p className="text-muted-foreground">
              {isItalian ? 'Caricamento report…' : 'Loading report…'}
            </p>
          </div>
        )}

        {error && (
          <div className="max-w-md mx-auto text-center py-16">
            <h2 className="text-xl font-bold text-foreground mb-2">
              {isItalian ? 'Report non disponibile' : 'Report not available'}
            </h2>
            <p className="text-muted-foreground">
              {error === 'not_found'
                ? isItalian
                  ? 'Il report richiesto non esiste o non è ancora stato generato.'
                  : 'The requested report does not exist or has not been generated yet.'
                : isItalian
                  ? 'Si è verificato un errore. Riprova più tardi.'
                  : 'Something went wrong. Please try again later.'}
            </p>
          </div>
        )}

        {!loading && !error && payload && payload.assessmentType === 'automation' && (
          <ReportDisplay
            report={payload.report as Parameters<typeof ReportDisplay>[0]['report']}
            language={language}
            isLoading={false}
            onNewAssessment={() => navigate('/')}
          />
        )}
        {!loading && !error && payload && payload.assessmentType === 'startup' && (
          <StartupReportDisplay
            report={payload.report as Parameters<typeof StartupReportDisplay>[0]['report']}
            language={language}
            isLoading={false}
            onNewAssessment={() => navigate('/')}
          />
        )}
        {!loading && !error && payload && payload.assessmentType === 'project' && (
          <ProjectReportDisplay
            report={payload.report as Parameters<typeof ProjectReportDisplay>[0]['report']}
            language={language}
            isLoading={false}
            onNewAssessment={() => navigate('/')}
          />
        )}
      </main>
    </div>
  );
}

function App() {
  const route = useRoute();

  if (route === '/admin') {
    return <AdminPanel />;
  }

  if (route === '/automation-assessment') {
    return <MainApp />;
  }

  if (route === '/startup') {
    return <StartupApp />;
  }

  if (route === '/project') {
    return <ProjectApp />;
  }

  // Public shareable report: /r/<uuid>
  const publicReportMatch = route.match(/^\/r\/([A-Za-z0-9-]+)\/?$/);
  if (publicReportMatch) {
    return <PublicReport reportId={publicReportMatch[1]} />;
  }

  return <HomePage />;
}

export default App;
